import { useEffect, useRef, useState } from 'react';

/**
 * Enforces the "show a skeleton for a minimum of 150ms even if data arrives
 * instantly" global rule (TA-10089). A sub-150ms flash of skeleton reads as
 * a rendering bug, so this holds `visible=true` for `minMs` after `active`
 * flips to false, and immediately re-shows if `active` flips back to true
 * before the hold timer elapses.
 *
 * Usage: `const showSkeleton = useMinimumVisible(loading);` — render the
 * skeleton while `showSkeleton` is true, real content otherwise.
 */
export function useMinimumVisible(active: boolean, minMs = 150): boolean {
  const [visible, setVisible] = useState(active);
  const [prevActive, setPrevActive] = useState(active);
  const activatedAtRef = useRef<number | null>(active ? Date.now() : null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // TA-10617: `active` flipping false→true must flip `visible` true within
  // this same render, not on a later effect-driven re-render. A caller like
  // ChatMessageList switches to a different (loading) chat by changing
  // props/state on an already-mounted component — no remount resets
  // `useState(active)` — so without this, there is one committed paint where
  // `active` is already true but `visible` still holds its stale prior
  // value, showing whatever the caller renders as "not yet visible" (e.g. a
  // spinner) for a frame before the effect below catches up.
  if (active !== prevActive) {
    setPrevActive(active);
    if (active) {
      activatedAtRef.current = Date.now();
      setVisible(true);
    }
  }

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (active) {
      return;
    }

    const activatedAt = activatedAtRef.current;
    const elapsed = activatedAt === null ? minMs : Date.now() - activatedAt;
    const remaining = Math.max(minMs - elapsed, 0);

    if (remaining === 0) {
      setVisible(false);
      return;
    }

    timerRef.current = setTimeout(() => {
      setVisible(false);
      timerRef.current = null;
    }, remaining);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, minMs]);

  return visible;
}
